import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Explore6.css';
import Header from '../../components/Header';
import { userService, authService, therapistService } from '../../services';
import ExpertCard from '../../components/ExpertCard';

const Explore6 = () => {
  const [, setSelectedButton] = useState(null);
  const navigate = useNavigate();

  const handleButtonClick = (buttonName) => {
    setSelectedButton(buttonName);
    console.log('Clicked:', buttonName);
    
    // Add navigation based on button clicked
    switch(buttonName) {
      case 'Get Started':
        navigate('/book-expert');
        break;
      case 'View Insights':
        // Could navigate to a detailed insights page if it exists
        console.log('Viewing detailed insights...');
        break;
      case 'Share Results':
        // Could implement social sharing functionality
        console.log('Sharing results...');
        break;
      case 'Download Report':
        // Could implement PDF download functionality
        console.log('Downloading report...');
        break;
      case 'Learn More':
        navigate('/book-expert');
        break;
      default:
        console.log('Unknown button action');
    }
  };

  const handleTakeAssessmentAgain = () => {
    console.log('Navigating to /explore');
    // Use router navigation instead of forcing a full reload so app state and SPA routing remain consistent
    navigate('/explore');
  };

  const handleUserProfile = () => {
    navigate('/profile');
  };

  const handleSaveResults = async () => {
    // Build payload and call save
    const currentUser = authService.getCurrentUser();
    if (!currentUser?.id) {
      alert('Please login to save your results.');
      navigate('/login');
      return;
    }

    const payload = psychometricResults.map(r => ({
      trait: r.trait,
      score: r.score,
      change: r.change,
      description: r.description
    }));
    try {
      document.body.style.cursor = 'wait';
      await userService.savePsychometric(currentUser.id, { results: payload });
      alert('Kết quả đã được lưu thành công.');
    } catch (err) {
      console.error('Save psychometric error:', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to save results');
    } finally {
      document.body.style.cursor = 'default';
    }
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [experts, setExperts] = useState([]);
  const [therapists, setTherapists] = useState([]);
  // debug: raw response toggle removed when SHOW_DEBUG gating is used
  const [debugInfo, setDebugInfo] = useState({ status: null, normalizedCount: 0, rawPreview: [], rawResponse: null, token: null });
  const BUILD_TAG = 'API-THERAPISTS-V2';
  const BUILD_TIME = new Date().toISOString();
  // Debug visibility: can be forced with REACT_APP_SHOW_DEBUG env var ("true"/"false").
  // Default: visible in development, hidden in production.
  const SHOW_DEBUG = (typeof process !== 'undefined' && process?.env?.REACT_APP_SHOW_DEBUG)
    ? String(process.env.REACT_APP_SHOW_DEBUG).toLowerCase() === 'true'
    : (typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true);

  // Robust extractors for name and years of experience.
  // Backend may return these in many shapes (snake_case, camelCase, nested user/profile objects).
  const extractName = (r) => {
    if (!r) return '';
    // common top-level variants
    const candidates = [
      r.display_name,
      r.displayName,
      r.DisplayName,
      r.name,
      r.fullName,
      r.userName,
      r.title,
      r.title_name
    ];
    for (const c of candidates) if (c) return String(c);

    // nested user/profile shapes
    const nested = r.user || r.User || r.profile || r.Profile || r.person || r.person_profile;
    if (nested) {
      const nc = nested.display_name || nested.displayName || nested.full_name || nested.fullName || nested.name || (nested.first_name && nested.last_name ? `${nested.first_name || ''} ${nested.last_name || ''}`.trim() : null);
      if (nc) return String(nc);
    }

    // try to combine first/last
    if ((r.first_name || r.firstName) || (r.last_name || r.lastName)) {
      return `${r.first_name || r.firstName || ''} ${r.last_name || r.lastName || ''}`.trim();
    }

    // fallback to specialization or empty
    if (Array.isArray(r.specialization) && r.specialization.length) return String(r.specialization[0]);
    if (r.specialty) return String(r.specialty);
    return '';
  };

  const extractYears = (r) => {
    if (!r) return null;
    const candidates = [
      r.years_of_experience,
      r.yearsOfExperience,
      r.YearsOfExperience,
      r.experience_years,
      r.experienceYears,
      r.experience,
      r.experienceYearsCount,
      r.years,
      r.exp
    ];
    for (const c of candidates) {
      if (c === 0) return 0;
      if (c) {
        // if numeric already
        if (typeof c === 'number') return c;
        // if string, try to extract first number
        const s = String(c);
        const m = s.match(/(\d+(?:[.,]\d+)?)/);
        if (m) return Number(m[1].replace(',', '.'));
        // otherwise return the raw string
        return s;
      }
    }

    // check nested shapes
    const nested = r.profile || r.Profile || r.user || r.User || r.cv || r.CV;
    if (nested) {
      const v = nested.years_of_experience || nested.yearsOfExperience || nested.experienceYears || nested.experience;
      if (v === 0) return 0;
      if (v) {
        if (typeof v === 'number') return v;
        const ms = String(v).match(/(\d+(?:[.,]\d+)?)/);
        if (ms) return Number(ms[1].replace(',', '.'));
        return String(v);
      }
    }
    return null;
  };

  useEffect(() => {
    // Mirror BookExpert behavior: fetch via therapistService and normalize to same expert shape
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await therapistService.getAll();
        const raw = Array.isArray(res) ? res : res?.data || [];

        // helpers copied from BookExpert to produce identical expert objects
        const extractNameLocal = extractName;
        const extractYearsLocal = extractYears;

        const normalized = raw.map(r => {
          const specialties = Array.isArray(r.specialization) ? r.specialization : (r.specializations || r.specialty ? String(r.specialization || r.specializations || r.specialty).split(',').map(s=>s.trim()) : []);
          const yearsRaw = extractYearsLocal(r);
          const experienceStr = (typeof yearsRaw === 'number') ? `${yearsRaw}` : (typeof yearsRaw === 'string' && yearsRaw.length ? yearsRaw : null);
          const qualifications = Array.isArray(r.qualifications) ? r.qualifications : (r.qualifications || r.certifications || r.degrees || r.credentials ? String(r.qualifications || r.certifications || r.degrees || r.credentials).split(',').map(s=>s.trim()) : []);
          return {
            id: r.id || r._id || null,
            name: extractNameLocal(r) || '',
            specialty: (specialties[0]) || r.specialty || '',
            specialties,
            experience: experienceStr,
            rating: r.rating ?? r.Rating ?? null,
            price: r.price || r.hourly_rate || null,
            image: r.image || r.avatar || null,
            description: r.description || r.bio || '',
            // Ensure the ternary uses the combined condition rather than relying on operator precedence
            availability: (r.availability || r.isAcceptingClients) ? 'Có lịch' : 'Không rõ',
            qualifications,
            raw: r
          };
        });
        if (!mounted) return;
        setExperts(normalized);
        // Also keep therapists for backward debug/raw display
        setTherapists(normalized.map(n => ({ id: n.id, name: n.name, specialization: n.specialties, qualifications: n.qualifications, years: n.experience, hourlyRate: n.price, isAccepting: n.availability === 'Có lịch', raw: n.raw })));
        setDebugInfo({ status: 200, normalizedCount: normalized.length, rawPreview: (raw || []).slice(0,3), rawResponse: raw, token: localStorage.getItem('token') });
      } catch (err) {
        console.error('Failed to load therapists in Explore6', err);
        setError(err?.message || 'Failed to load therapists');
        setDebugInfo(prev => ({ ...prev, status: null, rawResponse: null, rawPreview: [], token: localStorage.getItem('token') }));
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const psychometricResults = [
    {
      trait: 'Openness',
      score: '75%',
      change: '+5% from last assessment',
      description: 'You are open to new experiences and ideas. High levels of openness suggest a creative and imaginative mind.',
      hasChange: true
    },
    {
      trait: 'Conscientiousness',
      score: '65%',
      change: null,
      description: 'You take your responsibilities seriously. High conscientiousness often leads to success and reliability.',
      hasChange: false
    },
    {
      trait: 'Extraversion',
      score: '80%',
      change: '-2% from last assessment',
      description: 'You enjoy social interactions and often feel energized in social situations.',
      hasChange: true
    },
    {
      trait: 'Agreeableness',
      score: '70%',
      change: null,
      description: 'You value harmony in your relationships with others and are usually considerate.',
      hasChange: false
    },
    {
      trait: 'Neuroticism',
      score: '40%',
      change: '-3% from last assessment',
      description: 'You tend to remain calm and composed under pressure. Low neuroticism indicates emotional stability.',
      hasChange: true
    }
  ];

  return (
    <div className="explore6-container">
      <Header />

      {/* Fixed debug overlay to verify the bundle is updated in the browser */}
      {SHOW_DEBUG && (
        <div style={{ position: 'fixed', right: 12, top: 12, zIndex: 9999, background: 'rgba(220,38,38,0.95)', color: '#fff', padding: '8px 12px', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', fontSize: 12 }}>
          <div style={{ fontWeight: 700 }}>{BUILD_TAG}</div>
          <div style={{ fontSize: 11, opacity: 0.95 }}>time: {BUILD_TIME}</div>
          <div style={{ fontSize: 11 }}>status: {debugInfo.status ?? 'n/a'}</div>
          <div style={{ fontSize: 11 }}>count: {debugInfo.normalizedCount}</div>
        </div>
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* Congratulations Section */}
        <div className="congratulations-section">
          <h1 className="congratulations-title">Congratulations!</h1>
          <p className="congratulations-subtitle">
            You've successfully completed your registration and psychometric assessment.
          </p>
          <div className="congratulations-buttons">
            <button 
              className="take-assessment-btn"
              onClick={handleTakeAssessmentAgain}
            >
              Take Assessment Again
            </button>
            <button 
              className="user-profile-btn"
              onClick={handleUserProfile}
            >
              User Profile
            </button>
            <button
              id="top-save-results-btn"
              className="take-assessment-btn"
              onClick={handleSaveResults}
            >
              Lưu kết quả
            </button>
          </div>
        </div>

        {/* Therapist Profile Section (from API) */}
  <div className="therapist-section experts-section">
          {SHOW_DEBUG && (
            <div style={{ background: '#fff7ed', border: '1px solid #ffedd5', padding: 12, borderRadius: 8, marginBottom: 12, textAlign: 'left' }}>
              <strong>Debug</strong>
              <div style={{ marginTop: 8 }}>
                <div><strong>HTTP status:</strong> {debugInfo.status ?? 'n/a'}</div>
                <div><strong>Token present:</strong> {debugInfo.token ? 'yes' : 'no'}</div>
                <div><strong>Normalized count:</strong> {debugInfo.normalizedCount}</div>
                <div style={{ marginTop: 8 }}><strong>Preview (first 3):</strong></div>
                {debugInfo.rawPreview && debugInfo.rawPreview.length > 0 ? (
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fff', padding: 8, borderRadius: 6, marginTop: 6 }}>
                    {JSON.stringify(debugInfo.rawPreview, null, 2)}
                  </pre>
                ) : (
                  <div style={{ color: '#6b7280', marginTop: 6 }}>No preview available.</div>
                )}
              </div>
            </div>
          )}
          {loading ? (
            <div>Loading therapists...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : therapists.length === 0 ? (
            <div className="no-therapists">No therapists available.</div>
          ) : (
            <>
              <div className="experts-header">
                <h2>Các nhà trị liệu chuyên gia của chúng tôi</h2>
                <div className="expert-nav" style={{ visibility: 'hidden' }}>
                  <button className="nav-btn prev-btn">‹</button>
                  <button className="nav-btn next-btn">›</button>
                </div>
              </div>

              <div className="experts-grid">
                {experts.map(expert => (
                  <ExpertCard
                    key={expert.id || expert.name}
                    expert={expert}
                    isFavorite={false}
                    onToggleFavorite={() => {}}
                    onBook={() => handleButtonClick('Đặt Lịch')}
                    bookLabel="Đặt Lịch"
                  />
                ))}
              </div>

              {SHOW_DEBUG && (
                <pre style={{ textAlign: 'left', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 16, background: '#f6f8fa', padding: 12, borderRadius: 6 }}>
                  {JSON.stringify(therapists, null, 2)}
                </pre>
              )}
            </>
          )}
        </div>

        {/* Psychometric Results Section */}
        <div className="results-section">
          <h2 className="results-title">Your Psychometric Results</h2>
          <p className="results-subtitle">Here are your results based on the assessment.</p>
          <div className="results-actions">
            <button 
              className="share-results-btn"
              onClick={() => handleButtonClick('Share Results')}
            >
              Share Results
            </button>
            <button 
              id="save-results-btn"
              className="take-assessment-btn save-results-btn"
              onClick={async () => {
                // Build payload and call save
                const currentUser = authService.getCurrentUser();
                if (!currentUser?.id) {
                  alert('Please login to save your results.');
                  navigate('/login');
                  return;
                }

                const payload = psychometricResults.map(r => ({
                  trait: r.trait,
                  score: r.score,
                  change: r.change,
                  description: r.description
                }));

                try {
                  // simple loading UX
                  document.body.style.cursor = 'wait';
                  await userService.savePsychometric(currentUser.id, { results: payload });
                  alert('Psychometric results saved successfully.');
                } catch (err) {
                  console.error('Save psychometric error:', err);
                  alert(err?.response?.data?.message || err?.message || 'Failed to save results');
                } finally {
                  document.body.style.cursor = 'default';
                }
              }}
              >
              Lưu kết quả
            </button>
            <button 
              className="download-report-btn"
              onClick={() => handleButtonClick('Download Report')}
            >
              Download Report
            </button>
          </div>

          <div className="scores-grid">
            {psychometricResults.map((result, index) => (
              <div key={index} className="score-card">
                <div className="score-icon"></div>
                <div className="score-content">
                  <h4 className="trait-name">{result.trait}</h4>
                  <div className="score-value">{result.score}</div>
                  {result.hasChange && (
                    <div className="score-change">{result.change}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Understanding Your Score Section */}
        <div className="understanding-section">
          <h2 className="understanding-title">Understanding Your Score</h2>
          <p className="understanding-subtitle">
            Your results provide insight into your personality traits and how you might fit in different environments.
          </p>
          <button 
            className="learn-more-btn"
            onClick={() => handleButtonClick('Learn More')}
          >
            Learn More
          </button>

          <div className="traits-grid">
            {psychometricResults.map((result, index) => (
              <div key={index} className="trait-card">
                <div className="trait-icon"></div>
                <div className="trait-content">
                  <h4 className="trait-title">{result.trait}</h4>
                  <p className="trait-description">{result.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Product</h3>
            <ul>
              <li><a href="#">Pricing</a></li>
              <li><a href="#">Features</a></li>
              <li><a href="#">Integrations</a></li>
              <li><a href="#">API</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Solutions</h3>
            <ul>
              <li><a href="#">Brainstorming</a></li>
              <li><a href="#">Ideation</a></li>
              <li><a href="#">Wireframing</a></li>
              <li><a href="#">Research</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Resources</h3>
            <ul>
              <li><a href="#">Help Center</a></li>
              <li><a href="#">Blog</a></li>
              <li><a href="#">Tutorials</a></li>
              <li><a href="#">Support</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Support</h3>
            <ul>
              <li><a href="#">Contact Us</a></li>
              <li><a href="#">Status</a></li>
              <li><a href="#">Community</a></li>
              <li><a href="#">Feedback</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Company</h3>
            <ul>
              <li><a href="#">About</a></li>
              <li><a href="#">Careers</a></li>
              <li><a href="#">Press</a></li>
              <li><a href="#">Partners</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <span>All rights reserved.</span>
          </div>
          <div className="footer-bottom-right">
            <a href="#">Terms</a>
            <a href="#">Privacy</a>
            <a href="#">Contact</a>
            <div className="language-selector">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" fill="currentColor"/>
                <path d="M8 2c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" fill="currentColor"/>
              </svg>
              <span>EN</span>
            </div>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" fill="currentColor"/>
            </svg>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Explore6;

